import { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import TopNav from "@/components/TopNav";
import {
  login as apiLogin,
  sendOtp,
  verifyOtp,
  selectAccount,
  lookupCoupon,
  signup as apiSignup,
  requestPasswordReset,
  verifyPasswordReset,
  checkAccount,
  applyUpgradeCode,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { facebookStatus, facebookExchange, facebookLoginUrl, facebookCompleteEmail } from "@/lib/api";
import {
  Facebook,
  Loader2,
  Mail,
  Phone,
  Eye,
  EyeOff,
  ArrowLeft,
  Gift,
  CheckCircle2,
  Sparkles,
  ChevronRight,
} from "lucide-react";

const STEPS = {
  ENTRY: "entry",
  PASSWORD: "password",
  OTP: "otp",
  ACCOUNT_PICKER: "account-picker",
  SIGNUP: "signup",
  RESET_REQUEST: "reset-request",
  RESET_VERIFY: "reset-verify",
};

export default function Login() {
  const { user, login, adoptUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  // ----- Promo code -----
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null); // { ambassador_name, coupon_name, ... }
  const [promoLoading, setPromoLoading] = useState(false);

  // ----- Method (email/phone) -----
  const [method, setMethod] = useState("email");
  const [identifier, setIdentifier] = useState("");

  // ----- Step state -----
  const [step, setStep] = useState(STEPS.ENTRY);
  const [submitting, setSubmitting] = useState(false);
  const [postSignupPath, setPostSignupPath] = useState("");
  // ----- Returning-user welcome (set when the entered email matches an
  // existing account, so old customers realise they already have one) -----
  const [welcomeBack, setWelcomeBack] = useState(null); // { name, first_name, role } | null

  // ----- Email login -----
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // ----- Phone OTP -----
  const [otp, setOtp] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  // When a phone is shared across multiple Users records, verify-otp returns
  // a list of accounts and a short-lived select token — the caller picks one.
  const [accountChoices, setAccountChoices] = useState([]);
  const [selectToken, setSelectToken] = useState("");

  // ----- Sign up extras -----
  const [signupData, setSignupData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });

  // ----- Reset password -----
  const [resetEmail, setResetEmail] = useState("");
  const [resetVia, setResetVia] = useState("email");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPwd, setResetNewPwd] = useState("");

  // Sync step with ?signup query param (top nav tabs).
  // React 19's react-hooks rules forbid setState in render / refs / effects
  // here, so we use the "key" reconciliation pattern: a useState seeded
  // from the URL flag, plus a setSyncedStep helper invoked from event handlers.
  const isSignupQuery = searchParams.get("signup") === "1";
  const [lastSignupFlag, setLastSignupFlag] = useState(isSignupQuery);
  if (lastSignupFlag !== isSignupQuery) {
    // Setting state during render — supported per React docs when guarded by
    // an equality check; React will discard the in-progress render and retry.
    setLastSignupFlag(isSignupQuery);
    if (isSignupQuery) setStep(STEPS.SIGNUP);
    else if (step === STEPS.SIGNUP) setStep(STEPS.ENTRY);
  }

  // Where to go after a successful sign-in / sign-up. Honour ?next= when it is
  // a safe same-origin internal path (must start with "/" and not "//"), else
  // fall back to the storefront home (shop), not the dashboard.
  const rawNext = searchParams.get("next") || "";
  const nextPath =
    rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//")
      ? rawNext
      : "/d/orevital?view=shop";

  const [fbEnabled, setFbEnabled] = useState(false);
  const [fbBusy, setFbBusy] = useState(!!searchParams.get("fb_code"));
  const [fbPending, setFbPending] = useState(searchParams.get("fb_need_email") || "");
  const [fbEmail, setFbEmail] = useState("");
  const [fbEmailBusy, setFbEmailBusy] = useState(false);
  async function submitFbEmail(e) {
    e?.preventDefault?.();
    if (!fbEmail.trim()) return;
    setFbEmailBusy(true);
    try {
      const r = await facebookCompleteEmail(fbPending, fbEmail.trim());
      adoptUser(r.user || r);
      toast.success("Signed in with Facebook");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete sign-in");
      if ((err?.response?.data?.detail || "").includes("expired")) setFbPending("");
    } finally {
      setFbEmailBusy(false);
    }
  }
  useEffect(() => {
    facebookStatus().then((r) => setFbEnabled(!!r.configured)).catch(() => {});
  }, []);
  useEffect(() => {
    const code = searchParams.get("fb_code");
    const err = searchParams.get("fb_error");
    if (err) toast.error(err === "cancelled" ? "Facebook sign-in was cancelled" : `Facebook sign-in failed: ${err}`);
    if (!code) return;
    facebookExchange(code)
      .then((r) => { adoptUser(r.user || r); toast.success("Signed in with Facebook"); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Facebook sign-in failed"))
      .finally(() => setFbBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to={postSignupPath || nextPath} replace />;

  // ------ Handlers ------
  async function applyPromo() {
    const code = promo.trim();
    if (!code) return;
    setPromoLoading(true);
    try {
      const res = await lookupCoupon(code);
      if (res.valid) {
        setPromoInfo(res);
        toast.success(`${res.ambassador_name}'s code applied`);
      } else {
        setPromoInfo(null);
        toast.error(res.message || "Coupon not found");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not validate code");
    } finally {
      setPromoLoading(false);
    }
  }

  function removePromo() {
    setPromo("");
    setPromoInfo(null);
  }

  // After any successful sign-in, if the shopper entered a promo/upgrade code
  // on the login screen, apply it as a permanent account-role upgrade
  // (upward only; admin/dist never granted server-side). Non-blocking.
  async function maybeUpgradeFromPromo(u) {
    const code = promo.trim();
    const uRec = u?.record_id || u?.user_record_id;
    if (!code || !uRec) return u;
    try {
      const up = await applyUpgradeCode(uRec, code);
      if (up?.upgraded) {
        const upgraded = { ...u, role_name: up.new_role, role: up.new_role };
        adoptUser(upgraded);
        toast.success(`🎉 Your account has been upgraded to ${up.new_role}!`);
        return upgraded;
      }
    } catch {
      /* non-blocking: never block sign-in on an upgrade failure */
    }
    return u;
  }

  async function onContinue(e) {
    e?.preventDefault?.();
    const value = identifier.trim();
    if (!value) {
      toast.error(method === "email" ? "Enter your email" : "Enter your phone");
      return;
    }
    if (method === "email") {
      if (!value.includes("@")) {
        toast.error("Enter a valid email");
        return;
      }
      // Look up the account so returning users get a warm "welcome back"
      // banner on the password step (old customers often don't realise they
      // already have an account). Non-blocking: fall through on any error.
      setSubmitting(true);
      let res = null;
      try {
        res = await checkAccount(value);
        setWelcomeBack(res?.exists ? res : null);
      } catch {
        setWelcomeBack(null);
      } finally {
        setSubmitting(false);
      }
      if (res && res.exists === false) {
        // No account for this email → straight to sign-up, email pre-filled.
        toast.error("No account found for that email — let's create one.", { duration: 6000 });
        setSignupData((s) => ({ ...s, email: value }));
        setStep(STEPS.SIGNUP);
        return;
      }
      // Account exists (or lookup failed) → password step.
      setStep(STEPS.PASSWORD);
    } else {
      // Phone — request OTP
      setSubmitting(true);
      try {
        const res = await sendOtp(value);
        if (res.success) {
          setNormalizedPhone(res.phone);
          setStep(STEPS.OTP);
          toast.success("Code sent");
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not send code");
      } finally {
        setSubmitting(false);
      }
    }
  }

  async function onPasswordSubmit(e) {
    e?.preventDefault?.();
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(identifier.trim(), password, { remember: rememberMe });
      toast.success(`Welcome back, ${u.first_name || ""}`.trim());
      await maybeUpgradeFromPromo(u);
      nav(nextPath, { replace: true });
    } catch (err) {
      const msg = err.message || "Login failed";
      if (err.accountExists) {
        // Account exists but the password was wrong → send them straight to
        // password reset (don't push them into Sign Up — that confuses
        // existing customers into thinking they have no account).
        setResetEmail(identifier.trim());
        setResetVia("email");
        setStep(STEPS.RESET_REQUEST);
        toast.error("That password didn't match. Let's reset it.");
      } else if (/invalid/i.test(msg)) {
        // No account for this email → offer sign-up with it pre-filled.
        toast.error("No account found — let's create one.");
        setSignupData((s) => ({ ...s, email: identifier.trim(), password }));
        setStep(STEPS.SIGNUP);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onOtpSubmit(e) {
    e?.preventDefault?.();
    if (otp.length < 4) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      const res = await verifyOtp(normalizedPhone, otp);
      if (!res.success && !res.signup_required) {
        toast.error(res.message || "Invalid code");
        return;
      }
      if (res.signup_required) {
        setSignupData((s) => ({ ...s, phone: normalizedPhone }));
        setStep(STEPS.SIGNUP);
        toast("Verified — finish creating your account");
        return;
      }
      if (res.accounts_required) {
        setAccountChoices(res.accounts || []);
        setSelectToken(res.select_token || "");
        setStep(STEPS.ACCOUNT_PICKER);
        return;
      }
      adoptUser(res.user || res);
      toast.success(`Welcome, ${res.user?.first_name || ""}`.trim());
      await maybeUpgradeFromPromo(res.user || res);
      nav(nextPath, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onAccountSelect(recordId) {
    setSubmitting(true);
    try {
      const res = await selectAccount(selectToken, recordId);
      adoptUser(res.user || res);
      toast.success(`Welcome, ${res.user?.first_name || ""}`.trim());
      await maybeUpgradeFromPromo(res.user || res);
      nav(nextPath, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSignupSubmit(e) {
    e?.preventDefault?.();
    const s = signupData;
    if (!s.first_name.trim()) return toast.error("First name is required");
    if (!s.email.trim() && !s.phone.trim())
      return toast.error("Email or phone is required");
    if (!s.password) return toast.error("Choose a password");
    if (s.password !== s.confirm)
      return toast.error("Passwords don't match");

    setSubmitting(true);
    try {
      // Storefront signup: if the user arrived from /d/<slug>, attribute them to
      // that dealer (Contacts = source of truth) even without a promo code.
      const dealerSlugMatch = /^\/d\/([^/?#]+)/.exec(rawNext || "");
      const dealerSlug = dealerSlugMatch ? decodeURIComponent(dealerSlugMatch[1]) : null;
      const res = await apiSignup({
        first_name: s.first_name.trim(),
        last_name: s.last_name.trim(),
        email: s.email.trim() || identifier.trim(),
        phone: s.phone || normalizedPhone,
        password: s.password,
        referral_code: promoInfo?.coupon_name || null,
        dealer_slug: dealerSlug,
        phone_verified: !!normalizedPhone,
      });
      const slug = res.redirect_slug || res.user?.redirect_slug || "orevital";
      setPostSignupPath(`/d/${slug}?view=shop`);
      adoptUser(res.user || res);
      toast.success(`Welcome, ${res.user?.first_name || s.first_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setSubmitting(false);
    }
  }

  function back() {
    if (step === STEPS.OTP || step === STEPS.PASSWORD) {
      setStep(STEPS.ENTRY);
      setOtp("");
      setPassword("");
    } else if (step === STEPS.ACCOUNT_PICKER) {
      // Restart the OTP flow if the caller decides to pick from a different
      // phone — they'll need a fresh code.
      setStep(STEPS.ENTRY);
      setOtp("");
      setAccountChoices([]);
      setSelectToken("");
    } else if (step === STEPS.SIGNUP) {
      // Pop back to entry, but keep promo code
      setStep(STEPS.ENTRY);
    } else if (step === STEPS.RESET_REQUEST) {
      setStep(STEPS.PASSWORD);
    } else if (step === STEPS.RESET_VERIFY) {
      setStep(STEPS.RESET_REQUEST);
    }
  }

  async function onResetRequest(e) {
    e?.preventDefault?.();
    const target = (resetEmail || identifier).trim();
    const isSms = resetVia === "sms";
    if (isSms) {
      const digits = target.replace(/[^\d+]/g, "");
      if (digits.replace("+", "").length < 7) {
        toast.error("Enter a valid phone number");
        return;
      }
    } else if (!target.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setResetEmail(target);
    setSubmitting(true);
    try {
      await requestPasswordReset(target, isSms ? "SMS" : "Email");
      toast.success(
        isSms ? "If we found you, an SMS was sent" : "If the email exists, a code has been sent"
      );
      setStep(STEPS.RESET_VERIFY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setSubmitting(false);
    }
  }

  async function onResetVerify(e) {
    e?.preventDefault?.();
    if (resetCode.length < 6) return toast.error("Enter the 6-digit code");
    if (resetNewPwd.length < 6)
      return toast.error("Password must be at least 6 characters");
    setSubmitting(true);
    try {
      const res = await verifyPasswordReset(resetEmail, resetCode, resetNewPwd);
      if (!res.success) {
        toast.error(res.message || "Invalid code");
        return;
      }
      // Sign in with the new password (remember by default after reset)
      const u = await login(resetEmail, resetNewPwd, { remember: true });
      toast.success(`Password updated. Welcome back, ${u.first_name || ""}`.trim());
      await maybeUpgradeFromPromo(u);
      nav(nextPath, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ------ Render ------
  return (
    <div className="min-h-dvh bg-white flex flex-col" data-testid="login-page">
      <TopNav variant="auth" />
      <div className="flex-1 px-6 pt-8 pb-6 flex flex-col max-w-md mx-auto w-full">
        {step !== STEPS.ENTRY && (
          <button
            onClick={back}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 mb-4 -ml-1"
            data-testid="auth-back-btn"
          >
            <ArrowLeft size={16} /> Back
          </button>
        )}

        {step === STEPS.ENTRY && fbPending && (
          <form onSubmit={submitFbEmail} className="space-y-3" data-testid="fb-email-form">
            <div className="text-sm text-zinc-700">
              Almost done — Facebook didn&apos;t share an email address. We need one for your account, order updates and rewards.
            </div>
            <Input
              type="email"
              required
              autoFocus
              value={fbEmail}
              onChange={(e) => setFbEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-12 rounded-xl"
              data-testid="fb-email-input"
            />
            <Button type="submit" disabled={fbEmailBusy || !fbEmail.trim()} className="w-full h-12 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold" data-testid="fb-email-submit">
              {fbEmailBusy ? <Loader2 className="animate-spin" size={16} /> : "Finish signing in"}
            </Button>
            <button type="button" onClick={() => setFbPending("")} className="w-full text-xs text-zinc-500 hover:underline" data-testid="fb-email-cancel">Use email / phone instead</button>
          </form>
        )}
        {step === STEPS.ENTRY && !fbPending && (
          <EntryForm
            promo={promo}
            setPromo={setPromo}
            promoInfo={promoInfo}
            promoLoading={promoLoading}
            applyPromo={applyPromo}
            removePromo={removePromo}
            method={method}
            setMethod={setMethod}
            identifier={identifier}
            setIdentifier={setIdentifier}
            onContinue={onContinue}
            submitting={submitting}
            onForgotPassword={() => {
              setResetEmail(identifier.trim());
              setResetVia(method === "phone" ? "sms" : "email");
              setStep(STEPS.RESET_REQUEST);
            }}
            facebook={fbEnabled ? { busy: fbBusy, onClick: () => { window.location.href = facebookLoginUrl(nextPath); } } : null}
          />
        )}

        {step === STEPS.PASSWORD && (
          <PasswordStep
            email={identifier.trim()}
            welcomeBack={welcomeBack}
            password={password}
            setPassword={setPassword}
            showPwd={showPwd}
            setShowPwd={setShowPwd}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            onSubmit={onPasswordSubmit}
            submitting={submitting}
            switchToSignUp={() => {
              setSignupData((s) => ({ ...s, email: identifier.trim() }));
              setStep(STEPS.SIGNUP);
            }}
            switchToReset={() => {
              setResetEmail(identifier.trim());
              setStep(STEPS.RESET_REQUEST);
            }}
          />
        )}

        {step === STEPS.RESET_REQUEST && (
          <ResetRequestStep
            email={resetEmail}
            setEmail={setResetEmail}
            resetVia={resetVia}
            setResetVia={setResetVia}
            onSubmit={onResetRequest}
            submitting={submitting}
          />
        )}

        {step === STEPS.RESET_VERIFY && (
          <ResetVerifyStep
            email={resetEmail}
            code={resetCode}
            setCode={setResetCode}
            newPwd={resetNewPwd}
            setNewPwd={setResetNewPwd}
            onSubmit={onResetVerify}
            submitting={submitting}
          />
        )}

        {step === STEPS.OTP && (
          <OtpStep
            phone={normalizedPhone}
            otp={otp}
            setOtp={setOtp}
            onSubmit={onOtpSubmit}
            submitting={submitting}
            resend={async () => {
              try {
                await sendOtp(normalizedPhone);
                toast.success("New code sent");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not resend");
              }
            }}
          />
        )}

        {step === STEPS.ACCOUNT_PICKER && (
          <div data-testid="account-picker-step">
            <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
              Choose an account
            </h1>
            <p className="text-zinc-500 mt-2 text-base">
              This phone number is linked to more than one account. Pick which
              one to sign into.
            </p>
            <ul className="mt-6 space-y-2" data-testid="account-picker-list">
              {accountChoices.map((acc) => (
                <li key={acc.record_id}>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => onAccountSelect(acc.record_id)}
                    className="w-full text-left rounded-xl border border-zinc-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors px-4 py-3 flex items-center gap-3 disabled:opacity-60"
                    data-testid={`account-picker-row-${acc.record_id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold shrink-0">
                      {(acc.first_name?.[0] || "?").toUpperCase()}
                      {(acc.last_name?.[0] || "").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-900 truncate">
                        {acc.name}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {acc.email}
                        {acc.role ? ` · ${acc.role}` : ""}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-zinc-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === STEPS.SIGNUP && (
          <SignupStep
            data={signupData}
            setData={setSignupData}
            promoInfo={promoInfo}
            onSubmit={onSignupSubmit}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  );
}

function EntryForm({
  promo,
  setPromo,
  promoInfo,
  promoLoading,
  applyPromo,
  removePromo,
  method,
  setMethod,
  identifier,
  setIdentifier,
  onContinue,
  submitting,
  onForgotPassword,
  facebook = null,
}) {
  return (
    <>
      <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
        Sign in or create account
      </h1>
      <p className="text-zinc-500 mt-2 text-base">
        Use your email or phone to continue.
      </p>

      {/* Promo code block */}
      <div className="mt-8" data-testid="promo-code-block">
        <Label className="text-sm font-medium text-zinc-700">
          Promo Code <span className="text-zinc-400 font-normal">(optional)</span>
        </Label>
        {promoInfo ? (
          <div
            className="mt-1.5 rounded-xl border border-emerald-300 bg-emerald-50 p-3 flex items-start gap-3"
            data-testid="promo-applied"
          >
            <Gift size={18} className="text-emerald-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-800">
                {promoInfo.ambassador_name}&apos;s code applied!
              </div>
              <div className="text-xs text-emerald-700 flex items-center gap-1 mt-0.5">
                <CheckCircle2 size={12} />
                {promoInfo.coupon_name} · {promoInfo.coupon_tier}
              </div>
            </div>
            <button
              onClick={removePromo}
              className="text-xs text-emerald-700 underline"
              data-testid="promo-remove-btn"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="flex items-stretch gap-2 mt-1.5">
            <Input
              value={promo}
              onChange={(e) =>
                setPromo(e.target.value.toUpperCase().replace(/\s+/g, ""))
              }
              placeholder="Enter code"
              className="h-12 rounded-xl flex-1"
              data-testid="promo-code-input"
            />
            <Button
              type="button"
              onClick={applyPromo}
              disabled={promoLoading || !promo.trim()}
              className="h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-medium px-5"
              data-testid="promo-apply-btn"
            >
              {promoLoading ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
            </Button>
          </div>
        )}
      </div>

      <div className="my-7 border-t border-zinc-100" />

      <div className="text-sm font-semibold text-zinc-900 mb-3">
        Sign In / Create Account
      </div>

      {/* Method toggle */}
      <div
        className="grid grid-cols-2 gap-2 mb-4"
        data-testid="auth-method-toggle"
      >
        <MethodButton
          active={method === "email"}
          onClick={() => setMethod("email")}
          icon={Mail}
          label="Email"
          testId="auth-method-email"
        />
        <MethodButton
          active={method === "phone"}
          onClick={() => setMethod("phone")}
          icon={Phone}
          label="Phone"
          testId="auth-method-phone"
        />
      </div>

      <form onSubmit={onContinue}>
        {method === "email" ? (
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="h-14 text-base rounded-xl bg-violet-50/40 border-zinc-200 focus-visible:ring-violet-500"
            data-testid="auth-identifier-input"
          />
        ) : (
          <Input
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="(555) 123-4567"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="h-14 text-base rounded-xl bg-violet-50/40 border-zinc-200 focus-visible:ring-violet-500"
            data-testid="auth-identifier-input"
          />
        )}

        <Button
          type="submit"
          disabled={submitting}
          className="mt-4 w-full h-14 rounded-xl text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
          data-testid="auth-continue-btn"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : "Continue"}
        </Button>
      </form>

      {facebook && (
        <>
          <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-wide text-zinc-400">
            <span className="h-px flex-1 bg-zinc-200" /> or <span className="h-px flex-1 bg-zinc-200" />
          </div>
          <Button
            type="button"
            onClick={facebook.onClick}
            disabled={facebook.busy}
            className="w-full h-12 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold"
            data-testid="facebook-login-btn"
          >
            {facebook.busy ? <Loader2 className="animate-spin" size={16} /> : <><Facebook size={16} className="mr-2" /> Continue with Facebook / Instagram</>}
          </Button>
        </>
      )}

      <div className="mt-3 flex items-center justify-center">
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm font-medium text-violet-700 hover:underline"
          data-testid="forgot-password-entry-link"
        >
          Forgot password?
        </button>
      </div>

      <p className="mt-6 text-xs text-zinc-400 text-center">
        Approved OreVital / Mindful Minerals dealers and referrals.
      </p>
    </>
  );
}

function MethodButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-12 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${
        active
          ? "bg-emerald-600 text-white shadow-sm"
          : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );
}

// ============== PASSWORD STEP ==============
function PasswordStep({
  email,
  welcomeBack,
  password,
  setPassword,
  showPwd,
  setShowPwd,
  rememberMe,
  setRememberMe,
  onSubmit,
  submitting,
  switchToSignUp,
  switchToReset,
}) {
  const firstName = welcomeBack?.first_name || welcomeBack?.name || "";
  return (
    <>
      {welcomeBack ? (
        <div
          className="mb-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5"
          data-testid="welcome-back-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600">
              <Sparkles size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-emerald-900" data-testid="welcome-back-title">
                Welcome back{firstName ? `, ${firstName}` : ""}! 🎉
              </p>
              <p className="mt-1 text-sm text-emerald-800/80">
                Great news — you already have an OreVital account
                {welcomeBack?.name ? (
                  <> as <span className="font-medium">{welcomeBack.name}</span></>
                ) : null}
                . Just enter your password below to sign in.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
        {welcomeBack ? "Enter your password" : "Welcome back"}
      </h1>
      <p className="text-zinc-500 mt-1.5 text-sm">
        Sign in as <span className="font-medium text-zinc-700">{email}</span>
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="password-form">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="password" className="text-sm font-medium text-zinc-700">
              Password
            </Label>
            <button
              type="button"
              onClick={switchToReset}
              className="text-xs font-medium text-violet-700 hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPwd ? "text" : "password"}
              autoComplete="current-password"
              autoFocus
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-14 text-base rounded-xl pr-12"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              aria-label={showPwd ? "Hide password" : "Show password"}
              data-testid="login-toggle-password"
            >
              {showPwd ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <label
          className="flex items-center gap-2 text-sm text-zinc-700 select-none cursor-pointer"
          data-testid="login-remember-row"
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
            data-testid="login-remember-checkbox"
          />
          <span>Keep me signed in until I sign out</span>
        </label>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-14 rounded-xl text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="login-submit-btn"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
        </Button>

        <button
          type="button"
          onClick={switchToSignUp}
          className="block w-full h-11 rounded-xl border border-zinc-300 bg-white text-sm text-zinc-800 font-semibold hover:bg-zinc-50 transition-colors"
          data-testid="switch-to-signup-btn"
        >
          Don&apos;t have an account? Create one →
        </button>
      </form>
    </>
  );
}

// ============== RESET PASSWORD ==============
function ResetRequestStep({
  email,
  setEmail,
  resetVia,
  setResetVia,
  onSubmit,
  submitting,
}) {
  const isPhone = resetVia === "sms";
  return (
    <>
      <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
        Reset your password
      </h1>
      <p className="text-zinc-500 mt-1.5 text-sm">
        We&apos;ll send you a 6-digit code via {isPhone ? "SMS" : "email"}.
      </p>

      {/* Channel picker */}
      <div
        className="mt-5 inline-flex bg-zinc-100 rounded-xl p-1"
        data-testid="reset-channel-tabs"
      >
        <button
          type="button"
          onClick={() => setResetVia("email")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            !isPhone ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"
          }`}
          data-testid="reset-tab-email"
        >
          <Mail size={14} className="inline mr-1.5" />
          Email
        </button>
        <button
          type="button"
          onClick={() => setResetVia("sms")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            isPhone ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"
          }`}
          data-testid="reset-tab-sms"
        >
          <Phone size={14} className="inline mr-1.5" />
          SMS
        </button>
      </div>

      <form
        onSubmit={onSubmit}
        className="mt-6 space-y-5"
        data-testid="reset-request-form"
      >
        <div>
          <Label className="text-sm font-medium text-zinc-700">
            {isPhone ? "Phone number" : "Email"}
          </Label>
          <Input
            type={isPhone ? "tel" : "email"}
            inputMode={isPhone ? "tel" : "email"}
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={isPhone ? "+1 555 123 4567" : "you@example.com"}
            className="h-14 text-base rounded-xl mt-1.5"
            data-testid="reset-email-input"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-14 rounded-xl text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="reset-request-submit"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : "Send code"}
        </Button>
      </form>
    </>
  );
}

function ResetVerifyStep({
  email,
  code,
  setCode,
  newPwd,
  setNewPwd,
  onSubmit,
  submitting,
}) {
  return (
    <>
      <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
        Enter new password
      </h1>
      <p className="text-zinc-500 mt-1.5 text-sm">
        Check <span className="font-medium text-zinc-700">{email}</span> for a 6-digit
        code, then set a new password.
      </p>

      <form
        onSubmit={onSubmit}
        className="mt-8 space-y-5"
        data-testid="reset-verify-form"
      >
        <div>
          <Label className="text-sm font-medium text-zinc-700">6-digit code</Label>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            className="h-16 text-3xl text-center font-semibold tracking-[0.4em] rounded-xl mt-1.5"
            data-testid="reset-code-input"
          />
        </div>

        <div>
          <Label className="text-sm font-medium text-zinc-700">New password</Label>
          <Input
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            placeholder="At least 6 characters"
            className="h-14 text-base rounded-xl mt-1.5"
            data-testid="reset-new-password"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting || code.length < 6 || newPwd.length < 6}
          className="w-full h-14 rounded-xl text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="reset-verify-submit"
        >
          {submitting ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            "Set new password & sign in"
          )}
        </Button>
      </form>
    </>
  );
}

// ============== OTP STEP ==============
function OtpStep({ phone, otp, setOtp, onSubmit, submitting, resend }) {
  return (
    <>
      <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
        Enter your code
      </h1>
      <p className="text-zinc-500 mt-1.5 text-sm">
        We texted a 6-digit code to{" "}
        <span className="font-medium text-zinc-700">{phone}</span>
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="otp-form">
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          placeholder="123456"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="h-16 text-3xl text-center font-semibold tracking-[0.4em] rounded-xl"
          data-testid="otp-input"
        />
        <Button
          type="submit"
          disabled={submitting || otp.length < 6}
          className="w-full h-14 rounded-xl text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="otp-submit-btn"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : "Verify"}
        </Button>
        <button
          type="button"
          onClick={resend}
          className="block w-full text-center text-sm text-violet-700 font-medium hover:underline"
          data-testid="otp-resend-btn"
        >
          Resend code
        </button>
      </form>
    </>
  );
}

// ============== SIGNUP STEP ==============
function SignupStep({ data, setData, promoInfo, onSubmit, submitting }) {
  const [showPw, setShowPw] = useState(false);
  function update(field) {
    return (e) =>
      setData((d) => ({
        ...d,
        [field]: e.target.value,
      }));
  }
  return (
    <>
      <h1 className="text-3xl font-semibold text-zinc-900 leading-tight tracking-tight">
        Create your account
      </h1>
      <p className="text-zinc-500 mt-1.5 text-sm">
        {promoInfo
          ? `You'll be added under ${promoInfo.ambassador_name}.`
          : "We'll review your application within 24 hours."}
      </p>

      {promoInfo && (
        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm text-emerald-800">
          <Sparkles size={16} className="text-emerald-700" />
          <span>
            <span className="font-semibold">{promoInfo.coupon_name}</span> referral
            applied
          </span>
        </div>
      )}
      {!promoInfo && data.email && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="signup-no-account-notice">
          We couldn&apos;t find an account for <span className="font-semibold">{data.email}</span>. Fill in the form below to create one — it takes a minute.
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="signup-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-700">First name</Label>
            <Input
              value={data.first_name}
              onChange={update("first_name")}
              placeholder="Jane"
              className="h-12 rounded-xl mt-1"
              autoFocus
              data-testid="signup-first-name"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-zinc-700">Last name</Label>
            <Input
              value={data.last_name}
              onChange={update("last_name")}
              placeholder="Doe"
              className="h-12 rounded-xl mt-1"
              data-testid="signup-last-name"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium text-zinc-700">Email</Label>
          <Input
            type="email"
            inputMode="email"
            value={data.email}
            onChange={update("email")}
            placeholder="you@example.com"
            className="h-12 rounded-xl mt-1"
            data-testid="signup-email"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-zinc-700">Password</Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={data.password}
                onChange={update("password")}
                placeholder="••••••••"
                className="h-12 rounded-xl mt-1 pr-11"
                data-testid="signup-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-400 hover:text-zinc-700"
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="signup-toggle-password"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-zinc-700">Confirm</Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={data.confirm}
                onChange={update("confirm")}
                placeholder="••••••••"
                className="h-12 rounded-xl mt-1 pr-11"
                data-testid="signup-confirm"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-400 hover:text-zinc-700"
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="signup-toggle-confirm"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium text-zinc-700">Phone</Label>
          <Input
            type="tel"
            inputMode="tel"
            value={data.phone}
            onChange={update("phone")}
            placeholder="(555) 123-4567"
            className="h-12 rounded-xl mt-1"
            data-testid="signup-phone"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-14 rounded-xl text-base font-semibold bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="signup-submit-btn"
        >
          {submitting ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            "Create account"
          )}
        </Button>
      </form>
    </>
  );
}

// ============== PENDING STATE removed — auto-approve on signup ==============
